import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WorkspacePluginSetting {
  pluginId: string;
  enabled: boolean;
  settingsJson?: Record<string, unknown>;
}

interface PluginState {
  enabledIds: string[];
  seenPluginIds: string[];
  errors: Record<string, string>;
  booted: boolean;
  hydrated: boolean;
  /** Workspace-scoped plugin overrides keyed by pluginId. Fetched from server. */
  workspaceSettings: WorkspacePluginSetting[];
  workspaceSettingsLoadedFor: string | null;
  setEnabledIds: (ids: string[]) => void;
  setSeenPluginIds: (ids: string[]) => void;
  setErrors: (errors: Record<string, string>) => void;
  setBooted: (booted: boolean) => void;
  setHydrated: () => void;
  setWorkspaceSettings: (settings: WorkspacePluginSetting[]) => void;
  fetchWorkspaceSettings: (workspaceId: string, token?: string | null) => Promise<void>;
  saveWorkspaceSetting: (workspaceId: string, pluginId: string, enabled: boolean, token?: string | null, settingsJson?: Record<string, unknown>) => Promise<void>;
}

export const usePluginStore = create<PluginState>()(
  persist(
    (set) => ({
      enabledIds: [],
      seenPluginIds: [],
      errors: {},
      booted: false,
      hydrated: false,
      workspaceSettings: [],
      workspaceSettingsLoadedFor: null,

      setEnabledIds: (ids) => set({ enabledIds: ids }),
      setSeenPluginIds: (ids) => set({ seenPluginIds: ids }),
      setErrors: (errors) => set({ errors }),
      setBooted: (booted) => set({ booted }),
      setHydrated: () => set({ hydrated: true }),
      setWorkspaceSettings: (settings) => set({ workspaceSettings: settings }),

      fetchWorkspaceSettings: async (workspaceId, token) => {
        try {
          const headers: HeadersInit = {};
          if (token) headers.Authorization = `Bearer ${token}`;
          const res = await fetch(
            `/api/dotnet/workspaces/${workspaceId}/plugins/settings`,
            { headers }
          );
          if (!res.ok) {
            set({ workspaceSettings: [], workspaceSettingsLoadedFor: workspaceId });
            return;
          }
          const raw = (await res.json()) as Array<{
            pluginId: string;
            enabled: boolean;
            settingsJson?: string;
          }>;
          const settings: WorkspacePluginSetting[] = (Array.isArray(raw) ? raw : []).map((s) => ({
            ...s,
            settingsJson: s.settingsJson ? (JSON.parse(s.settingsJson) as Record<string, unknown>) : undefined,
          }));
          set({ workspaceSettings: settings, workspaceSettingsLoadedFor: workspaceId });
        } catch {
          set({ workspaceSettings: [], workspaceSettingsLoadedFor: workspaceId });
        }
      },

      saveWorkspaceSetting: async (workspaceId, pluginId, enabled, token, settingsJson) => {
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const body: Record<string, unknown> = { enabled };
        if (settingsJson !== undefined) {
          body.settingsJson = JSON.stringify(settingsJson);
        }
        const res = await fetch(
          `/api/dotnet/workspaces/${workspaceId}/plugins/settings/${pluginId}`,
          { method: 'PUT', headers, body: JSON.stringify(body) }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rawUpdated = (await res.json()) as { pluginId: string; enabled: boolean; settingsJson?: string };
        const updated: WorkspacePluginSetting = {
          ...rawUpdated,
          settingsJson: rawUpdated.settingsJson ? (JSON.parse(rawUpdated.settingsJson) as Record<string, unknown>) : undefined,
        };
        set((s) => ({
          workspaceSettingsLoadedFor: workspaceId,
          workspaceSettings: [
            ...s.workspaceSettings.filter((x) => x.pluginId !== pluginId),
            updated,
          ],
        }));
      },
    }),
    {
      name: 'sopho-plugin-store',
      partialize: (state) => ({
        enabledIds: state.enabledIds,
        seenPluginIds: state.seenPluginIds,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    }
  )
);
